// UI
import ImageWithFallbacks from '@/components/ui/ImageWithFallbacks'
import FulfillmentOrderAssociateWarranty from '@/components/fulfillment_orders/FulfillmentOrderAssociateWarranty'
import SKU from '@/components/ui/SKU'

// Mantine
import { Anchor, Tooltip, ActionIcon } from '@mantine/core'

// Nextjs
// Icons
import { IconX, IconChecklist, IconAlertTriangle, IconPrinter } from '@tabler/icons-react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useState } from 'react'

export default function LineItemDisplay({
    line,
    warranties,
    associateWarranty,
    warrantyCodeToAssociate,
    loadComponent,
    fulfillment_order,
    markLineAsReady,
    markLineAsNotReady,
    onPrintLabel,
    action,
    hasVariantPhysicalStock,
    getOutOfStockComponentsForVariant,
    getComponentPhysicalStatus,
    availabilityResult,
    worksheetUrl,
}) {
    let title = line.name
    let subtitle = null
    let link = null
    let imageUrl = null

    const lineWarranties = (warranties || []).filter(
        (w) => w.shopify_product_variant_id === line.variant_id
    )

    if (line.component) {
        // Component line - format: "Catégorie — Nom"
        const categoryName = line.component.component_category?.name_fr
        title = categoryName
            ? `${categoryName} — ${line.component.name}`
            : line.component.name
        subtitle = null
        link = '/components/' + line.component.id
        imageUrl = line.component.image_url
    } else if (line.shopify_product_variant?.shopify_product) {
        // Shopify variant line
        title = line.shopify_product_variant?.shopify_product?.title
        subtitle =
            line.shopify_product_variant?.title === 'Default Title'
                ? '-'
                : line.shopify_product_variant?.title
        link =
            '/products/shopify/' +
            line.shopify_product_variant?.shopify_product?.id
        imageUrl = line.shopify_product_variant?.image_url
    }

    const buttonLabel = 'Associer une garantie'

    // Delete warranty
    const { requestDelete } = useAxiosApi()

    const [isDeleting, setIsDeleting] = useState(false)

    const deleteWarranty = async (code) => {
        setIsDeleting(true)

        await requestDelete(`/warranties/${code}`)
        await loadComponent()

        setIsDeleting(false)
    }

    // Warranty
    const watch_head_id =
        line.shopify_product_variant?.components?.find(
            (c) => c.category === 'watch_head'
        )?.id

    const can_associate_warranty = watch_head_id ? true : false

    const isComponent = !!line.component

    const quantity = line.remaining_quantity || line.quantity

    const hasWatchClockDetails =
        line.shopify_product_variant?.wcd_jewels && line.unit_price

    // Vérifier si le stock physique est insuffisant pour cette ligne
    const hasPhysicalStockIssue = (() => {
        if (!availabilityResult?.components) return false
        if (isComponent) {
            const comp = availabilityResult.components.find((c) => c.id === line.component_id)
            return comp && getComponentPhysicalStatus?.(comp) !== 'available'
        }
        return hasVariantPhysicalStock ? !hasVariantPhysicalStock(line.variant_id) : false
    })()

    return (
        <div key={line.id} className="flex flex-col px-3 py-2 even:bg-gray-50">
            <div
                className={`flex gap-5 w-full ${isComponent ? 'items-center' : 'items-start'}`}
            >
                {!isComponent && (
                    <div className="w-[75px] shrink-0 self-center">
                        <ImageWithFallbacks
                            images={[
                                imageUrl,
                                line.shopify_product_variant?.shopify_product
                                    ?.image_url,
                            ]}
                            width={140}
                            className="p-1"
                        />
                    </div>
                )}

                {isComponent && (
                    <div className="w-[75px] shrink-0 self-center">
                        <SKU component={line.component} size="sm" />
                    </div>
                )}

                <div
                    className={`flex flex-col flex-1 ${isComponent ? 'justify-center' : ''}`}
                >
                    <div className="flex items-center gap-1.5">
                        {link && (
                            <Anchor href={link}>
                                {title}
                            </Anchor>
                        )}

                        {!link && <span className="text-sm">{title}</span>}

                        {hasPhysicalStockIssue && (
                            <Tooltip label="Stock physique insuffisant">
                                <IconAlertTriangle size={16} className="text-orange-500 shrink-0" />
                            </Tooltip>
                        )}
                    </div>

                    {subtitle && (
                        <span className="text-gray-500 text-sm">
                            {subtitle}
                        </span>
                    )}

                    {!isComponent && (() => {
                        const skus = (line.shopify_product_variant?.components || [])
                            .filter((comp) => ['strap', 'watch_head', 'watchroll'].includes(comp.category))
                            .sort((a, b) => (a.category || '').localeCompare(b.category || ''))
                            .flatMap((comp) => {
                                const prefix = comp.component_category?.sku_prefix
                                if (!prefix) return []
                                const sku = prefix + String(comp.id).padStart(4, '0')
                                return [<span key={comp.id} className="text-xs text-gray-400 font-mono">{sku}</span>]
                            })
                        return skus.length > 0 ? <div className="flex gap-1.5 mt-1 flex-wrap">{skus}</div> : null
                    })()}

                    {(hasWatchClockDetails ||
                        (can_associate_warranty &&
                            (fulfillment_order?.warranties_mode === 'none' ||
                                fulfillment_order?.warranties_mode ===
                                    'blank'))) && (
                        <div className="mt-auto pt-3">
                            {can_associate_warranty &&
                                fulfillment_order?.warranties_mode ===
                                    'none' && (
                                    <span className="text-red-500 text-sm">
                                        Ne pas ajouter de cartes de garanties
                                    </span>
                                )}

                            {can_associate_warranty &&
                                fulfillment_order?.warranties_mode ===
                                    'blank' && (
                                    <span className="text-gray-400 text-sm">
                                        Ajouter {line.remaining_quantity}{' '}
                                        {line.remaining_quantity > 1 ? 'cartes de garanties vierges' : 'carte de garantie vierge'}
                                    </span>
                                )}

                            {/* Le lien vient du parent : la feuille douanière
                                est une pièce d'expédition, pas de ligne, et
                                seul le parent sait de quelle expédition il
                                s'agit. Absent sur une demande de préparation,
                                où rien n'est encore expédié. */}
                            {hasWatchClockDetails && worksheetUrl && (
                                <Anchor
                                    href={worksheetUrl}
                                    size="xs"
                                    target="_blank"
                                >
                                    US Breakdown Value
                                </Anchor>
                            )}
                        </div>
                    )}

                    {can_associate_warranty &&
                        fulfillment_order?.warranties_mode === 'associate' && (
                            <div className="flex flex-col mt-3">
                                <span className={`text-sm font-medium ${lineWarranties.length < line.remaining_quantity ? 'text-orange-500' : 'text-green-600'}`}>
                                    Garanties {lineWarranties.length}/
                                    {line.remaining_quantity}
                                </span>

                                {lineWarranties.length <
                                    line.remaining_quantity && (
                                    <div className="mt-2 flex flex-col">
                                        <div>
                                            <FulfillmentOrderAssociateWarranty
                                                line={line}
                                                watchHeadId={watch_head_id}
                                                associateWarranty={
                                                    associateWarranty
                                                }
                                                warrantyCodeToAssociate={
                                                    warrantyCodeToAssociate
                                                }
                                                size="xs"
                                                label={buttonLabel}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-x-3 gap-y-2 mt-3">
                                    {lineWarranties.map((warranty) => (
                                        <div
                                            key={warranty.code}
                                            className="flex items-center gap-1.5 bg-gray-100 text-gray-900 py-1 px-3 rounded-lg"
                                        >
                                            <span>{warranty.code}</span>

                                            <Tooltip
                                                label={
                                                    'Effacer la garantie ' +
                                                    warranty.code
                                                }
                                            >
                                                <ActionIcon
                                                    variant="light"
                                                    size="xs"
                                                    color="gray"
                                                    onClick={() =>
                                                        deleteWarranty(
                                                            warranty.code
                                                        )
                                                    }
                                                    loading={isDeleting}
                                                >
                                                    <IconX size={13} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                </div>

                <div className="flex items-center my-auto gap-5 ml-auto pl-5">
                    <div className="ml-auto flex gap-3 items-center self-center">
                        {onPrintLabel && line?.variant_id && (
                            <Tooltip
                                label="Imprimer l'étiquette code-barres"
                                openDelay={500}
                            >
                                <ActionIcon
                                    variant="light"
                                    color="gray"
                                    size="md"
                                    onClick={() => onPrintLabel(line)}
                                >
                                    <IconPrinter size="1rem" />
                                </ActionIcon>
                            </Tooltip>
                        )}

                        {markLineAsReady && !line?.is_ready && (
                            <Tooltip
                                label="Marquer la ligne comme préparée"
                                openDelay={500}
                            >
                                <ActionIcon
                                    variant="light"
                                    color="gray"
                                    size="md"
                                    className="hover:bg-blue-100 hover:text-blue-600"
                                    onClick={() => {
                                        line.is_ready = true
                                        markLineAsReady(line)
                                    }}
                                >
                                    <IconChecklist size="1rem" />
                                </ActionIcon>
                            </Tooltip>
                        )}

                        {markLineAsNotReady && line?.is_ready && (
                            <Tooltip
                                label="Marquer la ligne comme non préparée"
                                openDelay={500}
                            >
                                <ActionIcon
                                    variant="filled"
                                    size="md"
                                    onClick={() => {
                                        line.is_ready = false
                                        markLineAsNotReady(line)
                                    }}
                                >
                                    <IconChecklist size="1rem" />
                                </ActionIcon>
                            </Tooltip>
                        )}

                        {action}
                    </div>

                    <div className="ml-0 pr-5 w-[50px] self-center">
                        {quantity && (
                            <span className="text-lg font-mono text-gray-800">
                                ×{quantity}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
